/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'components/Modal/Modal';
import Form from 'components/Form/Form';
import Loader from 'components/Loader/Loader';
import useEsroApi from 'esro/api';
import './Login.scss';

/**
 * This is the Login component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Login({
  passwordLink,
  registerLink,
  required,
  fields,
  errors,
  labels,
  onError,
  onSuccess,
  ...rest
}) {
  const esro = useEsroApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function onSubmit({ username, password }) {
    setLoading(true);

    esro
      .signin(username, password)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch(({ message }) => {
        const error =
          message === 'User/Password invalid.'
            ? 'invalidUsernamePassword'
            : message;

        setLoading(false);
        setError(error);
        onError(error);
      });
  }

  function renderError() {
    if (!error) return null;

    const message = errors[error] || error;

    return (
      <span className="error" aria-live="polite">
        {message}
      </span>
    );
  }

  function renderLoader() {
    if (!loading) return null;

    return <Loader />;
  }

  if (!esro) return null;

  return (
    <Modal className="login" label={labels.close} {...rest}>
      <h2>Logg inn</h2>
      <Form
        required={required}
        fields={fields}
        errors={errors}
        submit={labels.submit}
        onChange={() => {
          setError(null);
        }}
        onSubmit={onSubmit}
      >
        <div className="message error">{renderError()}</div>
        <a
          className="new-passsord"
          href={passwordLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {labels.newPassword}
        </a>
      </Form>
      <div className="new-customer">
        <h3>{labels.newCustomer}</h3>
        <a href={registerLink} target="_blank" rel="noopener noreferrer">
          {labels.newProfile}
        </a>
      </div>
      {renderLoader()}
    </Modal>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Login.propTypes = {
  passwordLink: PropTypes.string.isRequired,
  registerLink: PropTypes.string.isRequired,
  required: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
    })
  ).isRequired,
  labels: PropTypes.shape({
    newPassword: PropTypes.string.isRequired,
    newCustomer: PropTypes.string.isRequired,
    newProfile: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    close: PropTypes.string.isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    required: PropTypes.string.isRequired,
    invalid: PropTypes.string.isRequired,
    invalidUsernamePassword: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Login.defaultProps = {
  fields: [],
  required: '',
  labels: {
    newPassword: 'New password',
    newCustomer: 'New customer',
    newProfile: 'New profile',
    submit: 'Sign In',
    close: 'Close',
  },
  errors: {
    required: 'Required {field}',
    invalid: 'Invalid {field}',
    invalidUsernamePassword: 'Invalid username or password',
  },
  onClose: () => {},
  onError: () => {},
  onSuccess: () => {},
};

export default Login;
